import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token storage utilities - can be replaced with more secure storage
const tokenStorage = {
    get: () => localStorage.getItem('token'),
    set: (token) => localStorage.setItem('token', token),
    remove: () => localStorage.removeItem('token')
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(tokenStorage.get());
    const [loading, setLoading] = useState(true);

    const refreshUser = useCallback(async () => {
        const storedToken = tokenStorage.get();
        if (storedToken) {
            try {
                const response = await axios.get(`${API}/auth/me`, {
                    headers: { Authorization: `Bearer ${storedToken}` }
                });
                setUser(response.data);
                return response.data;
            } catch (error) {
                tokenStorage.remove();
                setToken(null);
                setUser(null);
            }
        }
        return null;
    }, []); // API is module-level constant, axios is imported - no dependencies needed

    useEffect(() => {
        const initAuth = async () => {
            await refreshUser();
            setLoading(false);
        };
        initAuth();
    }, [refreshUser]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { token: newToken, user: userData } = response.data;
        tokenStorage.set(newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, username) => {
        const response = await axios.post(`${API}/auth/register`, { email, password, username });
        const { token: newToken, user: userData } = response.data;
        tokenStorage.set(newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
    };

    const logout = () => {
        tokenStorage.remove();
        setToken(null);
        setUser(null);
    };

    const getAuthHeaders = useCallback(() => ({
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    // Helper to check tier access
    const canAccessTier = useCallback((requiredTier) => {
        const tierHierarchy = { free: 0, pro: 1, premium: 2 };
        const userTierLevel = tierHierarchy[user?.tier || 'free'];
        const requiredLevel = tierHierarchy[requiredTier];
        return userTierLevel >= requiredLevel;
    }, [user?.tier]);

    const value = useMemo(() => ({ 
        user, 
        token, 
        loading, 
        login, 
        register, 
        logout, 
        getAuthHeaders,
        refreshUser,
        canAccessTier,
        isAuthenticated: !!user,
        userTier: user?.tier || 'free'
    }), [user, token, loading, getAuthHeaders, refreshUser, canAccessTier]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
